import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, Pressable, Platform } from 'react-native';
import { usePathname } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Text from '@/components/Typography/Text';

// Scans the DOM for sections created by DocsSection (elements with nativeID).
// Uses IntersectionObserver for scroll-spy and scrollIntoView for navigation.
// Web-only component.

export const DocsTOC = ({
  containerClass = 'docs-content-fade',
  accentColor = '#3B82F6',
}) => {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const pathname = usePathname();

  const [sections, setSections] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const observerRef = useRef(null);

  // Scan the DOM for section headings after route changes. Page content is
  // fetched asynchronously, so the first scan usually runs before sections
  // exist — keep a MutationObserver on the content container until they do.
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const scan = () => {
      const contentFade = document.querySelector(containerClass === 'docs-content-fade' ? '[data-docs-content]' : `.${containerClass}`);
      if (!contentFade) return false;

      const elements = contentFade.querySelectorAll('[id]');
      const found = [];

      elements.forEach((el) => {
        const id = el.getAttribute('id');
        if (!id) return;
        const titleEl = el.querySelector('[data-section-title], h2');
        if (titleEl) {
          found.push({ id, title: titleEl.textContent });
        }
      });

      setSections(found);
      if (found.length > 0) setActiveId(found[0].id);
      return found.length > 0;
    };

    setSections([]);
    setActiveId(null);

    const timer = setTimeout(scan, 200);

    let observer;
    let observerTimeout;
    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(() => {
        if (scan()) {
          observer.disconnect();
          clearTimeout(observerTimeout);
        }
      });
      const container = document.querySelector(containerClass === 'docs-content-fade' ? '[data-docs-content]' : `.${containerClass}`);
      if (container) {
        observer.observe(container, { childList: true, subtree: true });
        // Pages without sections have nothing to find — stop watching.
        observerTimeout = setTimeout(() => observer.disconnect(), 10000);
      }
    }

    return () => {
      clearTimeout(timer);
      clearTimeout(observerTimeout);
      observer?.disconnect();
    };
  }, [pathname, containerClass]);

  // Scroll-spy via IntersectionObserver
  useEffect(() => {
    if (Platform.OS !== 'web' || sections.length === 0) return;

    if (observerRef.current) observerRef.current.disconnect();

    const visibleIds = new Set();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute('id');
          if (entry.isIntersecting) {
            visibleIds.add(id);
          } else {
            visibleIds.delete(id);
          }
        });

        // Pick the first section (in DOM order) that is currently visible
        for (const s of sections) {
          if (visibleIds.has(s.id)) {
            setActiveId(s.id);
            break;
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 },
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [sections]);

  const scrollToSection = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  if (sections.length < 2) return null;

  return (
    <View
      style={{
        position: 'sticky',
        top: 100,
        width: 200,
        flexShrink: 0,
        paddingLeft: 24,
        paddingTop: 40,
        alignSelf: 'flex-start',
      }}
    >
      <Text
        tag="p"
        size="n"
        weight="semibold"
        color="secondary"
        style={{
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          marginBottom: 16,
        }}
      >
        On this page
      </Text>

      <View
        style={{
          borderLeftWidth: 2,
          borderLeftColor: isDark
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(0,0,0,0.06)',
        }}
      >
        {sections.map((section) => {
          const isActive = activeId === section.id;

          return (
            <Pressable
              key={section.id}
              onPress={() => scrollToSection(section.id)}
              accessibilityRole="link"
              accessibilityLabel={`Jump to ${section.title}`}
              accessibilityState={{ selected: isActive }}
              style={({ hovered }) => ({
                paddingVertical: 6,
                paddingLeft: 14,
                marginLeft: -2,
                borderLeftWidth: 2,
                borderLeftColor: isActive
                  ? accentColor
                  : hovered
                    ? isDark
                      ? 'rgba(255,255,255,0.15)'
                      : 'rgba(0,0,0,0.15)'
                    : 'transparent',
                transition: 'all 0.15s ease',
              })}
            >
              <Text
                tag="span"
                size="n"
                numberOfLines={2}
                style={{
                  color: isActive
                    ? accentColor
                    : colors.text_color_secondary,
                  transition: 'color 0.15s ease',
                }}
                weight={isActive ? 'medium' : 'normal'}
              >
                {section.title}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
};

export default DocsTOC;
